import { useContext, useEffect, useState } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { VibratoClientContext } from "./contexts"
import { Building, ShieldCheck, User, MapPin, Page, ArrowLeft, Copy, Trash } from "iconoir-react"
import { formatUsPhone } from "@/lib/phone"

export default function ViewBusinessVerification() {
  const { uuid } = useParams()
  const navigate = useNavigate()
  const vibratoClient = useContext(VibratoClientContext)
  const [data, setData] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [copied, setCopied] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    const run = async () => {
      if (!vibratoClient || !uuid) return
      try {
        setIsLoading(true)
        const resp = await vibratoClient.getBusinessVerificationRequest(uuid)
        setData(resp)
      } catch (e) {
        console.error("Failed to load verification request", e)
      } finally {
        setIsLoading(false)
      }
    }
    run()
  }, [uuid, vibratoClient])

  const copyCode = () => {
    if (data?.verification_code) {
      navigator.clipboard.writeText(data.verification_code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleDelete = async () => {
    if (!vibratoClient || !uuid) return

    const confirmed = window.confirm(
      "Are you sure you want to delete this verification request? This action cannot be undone."
    )

    if (!confirmed) return

    try {
      setIsDeleting(true)
      await vibratoClient.deleteBusinessVerificationRequest(uuid)
      navigate("/business")
    } catch (e) {
      console.error("Failed to delete verification request", e)
      alert("Failed to delete verification request. Please try again.")
    } finally {
      setIsDeleting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="h-full w-full p-6 md:p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600" />
      </div>
    )
  }

  if (!data) {
    return <div className="h-full w-full p-6 md:p-8">Failed to load verification request.</div>
  }

  const statusBadge = (status: string) => {
    const map: any = {
      approved: "bg-green-100 text-green-800 border-green-200",
      rejected: "bg-red-100 text-red-800 border-red-200",
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      cancelled: "bg-gray-100 text-gray-800 border-gray-200"
    }
    const cls = map[status] || "bg-gray-100 text-gray-800 border-gray-200"
    return (
      <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full border ${cls}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    )
  }

  return (
    <div className="h-full w-full p-6 md:p-8 flex flex-col gap-8">
      {/* Header with Back Button and Delete */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/business"
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="p-3 bg-purple-100 rounded-xl">
            <Building width="27" className="text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Business Verification</h1>
            <p className="text-gray-600">Request status and next steps</p>
          </div>
        </div>

        {data.status === "pending" && (
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2 w-fit"
          >
            <Trash className="w-4 h-4" />
            {isDeleting ? "Deleting..." : "Delete Request"}
          </button>
        )}
      </div>

      {/* Business & Status Card */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gray-100 rounded-xl">
                <Building className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <div className="text-xl font-semibold text-gray-900">{data.business?.name}</div>
                <div className="text-gray-600">
                  {[data.business?.city, data.business?.state].filter(Boolean).join(", ")}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  Submitted {new Date(data.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
            <div className="flex justify-center sm:justify-end">{statusBadge(data.status)}</div>
          </div>
        </div>
      </div>

      {/* Verification Code Card */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl shadow-lg border border-purple-200">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <ShieldCheck className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Your Verification Code</h2>
              <p className="text-sm text-gray-600">Use this code in your verification email</p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 sm:p-6 border border-purple-200">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="break-all font-mono text-xl font-extrabold tracking-wide text-purple-700 sm:text-3xl sm:tracking-[0.2em] lg:text-4xl lg:tracking-[0.3em]">
                {data.verification_code}
              </div>
              <button
                onClick={copyCode}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-purple-100 px-4 py-2 text-purple-700 transition-colors hover:bg-purple-200 sm:w-auto"
              >
                <Copy className="w-4 h-4" />
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>

          <div className="mt-6 p-4 bg-white rounded-xl border border-purple-200">
            <h3 className="font-semibold text-gray-900 mb-3">Email Instructions</h3>
            <p className="text-gray-700 mb-3">
              Send an email to{" "}
              <span className="font-semibold text-purple-700">business-verifications@getvibrato.com</span> from your
              account email with:
            </p>
            <ul className="text-sm text-gray-700 space-y-2 ml-4">
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-purple-400 rounded-full mt-2"></div>
                <span>
                  <strong>Subject:</strong> Business Verification Request - {data.verification_code}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-purple-400 rounded-full mt-2"></div>
                <span>Photo of your government-issued ID (driver's license, passport, etc.)</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-purple-400 rounded-full mt-2"></div>
                <span>
                  Business ownership proof: utility bill, lease agreement, bank statement showing business name/DBA,
                  business license/formation documents, or authorization letter on company letterhead
                </span>
              </li>
            </ul>
            <p className="text-xs text-gray-500 mt-3">
              Please redact sensitive account numbers where possible for your security.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <User className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">Contact Information</h2>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-sm font-medium text-gray-500 mb-1">Full Name</div>
              <div className="text-gray-900 font-medium">{data.contact_full_name || "—"}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500 mb-1">Phone</div>
              <div className="text-gray-900 font-medium">
                {data.contact_phone ? formatUsPhone(data.contact_phone) : "—"}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500 mb-1">Country</div>
              <div className="text-gray-900 font-medium">{data.contact_country || "—"}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Address Information */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">Address Information</h2>
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            <div>
              <div className="text-sm font-medium text-gray-500 mb-1">Street Address</div>
              <div className="text-gray-900 font-medium">
                {data.contact_address_line1 || "—"}
                {data.contact_address_line2 && (
                  <>
                    <br />
                    {data.contact_address_line2}
                  </>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">City</div>
                <div className="text-gray-900 font-medium">{data.contact_city || "—"}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">State/Province</div>
                <div className="text-gray-900 font-medium">{data.contact_state || "—"}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">Postal Code</div>
                <div className="text-gray-900 font-medium">{data.contact_postal_code || "—"}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Information */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <Page className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">Additional Information</h2>
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-6">
            <div>
              <div className="text-sm font-medium text-gray-500 mb-2">Verification Notes</div>
              <div className="text-gray-900 bg-gray-50 p-4 rounded-xl border border-gray-200 whitespace-pre-wrap">
                {data.verification_notes || "No additional notes provided."}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500 mb-2">Supporting Documents</div>
              <div className="text-gray-900 bg-gray-50 p-4 rounded-xl border border-gray-200 whitespace-pre-wrap">
                {data.supporting_documents || "No supporting documents listed."}
              </div>
            </div>
            {data.reviewed_at && (
              <div className="pt-4 border-t border-gray-200">
                <div className="text-sm font-medium text-gray-500 mb-2">Review Information</div>
                <div className="text-gray-900 bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <div className="text-sm">
                    <strong>Reviewed:</strong> {new Date(data.reviewed_at).toLocaleString()}
                  </div>
                  {data.review_notes && <div className="mt-2 text-sm whitespace-pre-wrap">{data.review_notes}</div>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
